import React, { Children, createContext, useContext, useEffect, useMemo, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import moment from 'moment'
import { FIT_CONTENT, FIT_TEXT } from './table-sizing'

export const ColumnIndex = ({ field }) => <React.Fragment />
export const ColumnText = ({ field }) => <React.Fragment />
export const ColumnDate = ({ field }) => <React.Fragment />
export const ColumnDateTime = ({ field }) => <React.Fragment />
export const ColumnUnixDateTime = ({ field }) => <React.Fragment />
export const ColumnSecond = ({ field }) => <React.Fragment />
export const ColumnMilliSecond = ({ field }) => <React.Fragment />
export const ColumnDuration = ({ field }) => <React.Fragment />
// A millisecond duration that picks its own unit from the magnitude, for
// figures whose scale is not known up front (a job run is routinely sub-second
// and occasionally minutes). ColumnMilliSecond is always HH:mm:ss, which
// renders every one of those fast runs as a meaningless 00:00:00.
export const ColumnDurationAuto = ({ field }) => <React.Fragment />
export const ColumnYesNo = ({ field }) => <React.Fragment />
export const ColumnSize = ({ field }) => <React.Fragment />
export const ColumnSelector = ({ field }) => <React.Fragment />
export const ColumnLink = ({ field }) => <React.Fragment />
export const ColumnFormat = ({ field }) => <React.Fragment />
export const ColumnMenu = ({ menu }) => <React.Fragment />
// Select mode's checkbox column (grid-selection.js): the grids add it
// themselves, first and pinned left, while their `selection` is selecting;
// a page never declares it.
export const ColumnSelect = ({ selection }) => <React.Fragment />
export const SELECT_COL_ID = 'select'

export const TableMenuContext = createContext(false)

// Cell renderers. AG Grid keeps a column's cells while its cellRenderer is
// the same function and draws them again from scratch (unmounted, mounted)
// when it is not, and a page's <Column*> children are new on every render of
// the page, so getColumns runs on every one: a renderer built inside it was
// new each time, and a poll, a minute clock or any state change redrew every
// such cell (flicker, a lost hover, an open row menu closing). So every
// renderer here is one function for the life of the app (or, for a
// duration, for its pair of fields), and a column whose cells come from a
// function the page passes (ColumnSelector's selector, ColumnMenu's menu,
// ColumnLink's to) reads the page's latest one through ColumnPropsContext:
// the page's re-render redraws those cells in place, never stale.
const renderIndex = row => <span>{row.node.rowIndex}</span>
const renderDate = row => (row.value ? moment(row.value).format('MM/DD/YYYY') : '')
const renderDateTime = row => (row.value ? moment(row.value).format('MM/DD/YYYY : hh:mm:ss a') : '')
// a value in Unix seconds (moment(v).unix(format) handed the seconds back)
const renderUnixDateTime = row => (row.value ? moment.unix(Number(row.value)).format('MM/DD/YYYY : hh:mm:ss a') : '')
const renderSecond = row => (row.value ? moment.utc(parseInt(row.value * 1000)).format('HH:mm:ss') : '')
const renderMilliSecond = row => (row.value ? moment.utc(parseInt(row.value)).format('HH:mm:ss') : '')
const renderDurationAuto = row => formatDurationAuto(row.value)
const renderYesNo = row => (row.value ? <span>Yes</span> : <span>No</span>)
const renderSize = row => formatSize(row.value)
const renderFormatMissing = () => 'RENDER PROP MISSING'

const durationRenderers = new Map()
const durationRenderer = (beganAt, endedAt) => {
  const key = JSON.stringify([beganAt, endedAt])
  if (!durationRenderers.has(key)) {
    durationRenderers.set(key, row => {
      if (row.data) {
        let end = row.data[endedAt]
        let start = row.data[beganAt]
        if (end && start) {
          var diff = moment(end) - moment(start)
          var duration = moment.duration(diff).asMilliseconds()
          return moment.utc(duration).format('HH:mm:ss')
        }
      }

      return ''
    })
  }
  return durationRenderers.get(key)
}

// The page's latest props of each of its columns, by column key
// (ColumnPropsProvider, around the grid in TableAgClient and
// TableAgInfinite). AG Grid draws its cells inside the React tree, so they
// read it like any context and redraw when the page renders again.
const ColumnPropsContext = createContext(null)

// A column's key: its type and field, and how many columns of the same type
// and field come before it. It names the same column across renders, also
// when the page adds or removes another column before it.
const columnKeys = items => {
  const seen = {}
  return items.map(item => {
    const base = `${item.type && item.type.name}:${item.props.field === undefined ? '' : item.props.field}`
    seen[base] = (seen[base] || 0) + 1
    return `${base}:${seen[base] - 1}`
  })
}

export const ColumnPropsProvider = ({ columns, children }) => {
  const value = useMemo(() => {
    const items = Children.toArray(columns)
    const keys = columnKeys(items)
    const byKey = {}
    items.forEach((item, i) => {
      byKey[keys[i]] = item.props
    })
    return byKey
  }, [columns])
  return <ColumnPropsContext.Provider value={value}>{children}</ColumnPropsContext.Provider>
}

// The column's latest props; without a provider, the functions its
// definition was built with (colDef.context.cell).
const useColumnPropsOf = colDef => {
  const byKey = useContext(ColumnPropsContext)
  const context = (colDef && colDef.context) || {}
  return (byKey && byKey[context.columnKey]) || context.cell || {}
}
const useColumnProps = row => useColumnPropsOf(row.colDef)

const SelectorCell = row => {
  const { selector } = useColumnProps(row)
  return selector && row.data ? selector(row.data) : ''
}

const LinkCell = row => {
  const { field, to } = useColumnProps(row)
  return <Link text={row.data[field]} to={to(row.data)} />
}

const MenuCell = row => {
  const { menu } = useColumnProps(row)
  if (row.data) {
    return (
      <TableMenuContext.Provider value={true}>
        {/* The actions cell is the actions': a click between its
            buttons never opens the row (row-click.js). */}
        <div className="d-flex h-100 justify-content-center" data-no-row-click>
          {menu && menu(row.data)}
        </div>
      </TableMenuContext.Provider>
    )
  }
  return ''
}

// Select mode's cells. A row's box ticks and unticks it (its click is its
// own, never the row's: row-click.js); the label around it fills the cell,
// so the whole cell is the target. A row whose block has not loaded yet has
// no data and no box. The page names a box through the selection's labelOf.
const SelectCell = row => {
  const { selection } = useColumnProps(row)
  if (!row.data || !selection) return ''
  const id = selection.idOf(row.data)
  const checked = selection.isSelected(id)
  const name = selection.labelOf ? selection.labelOf(row.data) : `row ${id}`
  return (
    <label className="tb-select">
      <input type="checkbox" className="form-check-input" aria-label={`Select ${name}`} checked={checked}
        disabled={!checked && selection.atMax} title={!checked && selection.atMax ? `At most ${selection.max.toLocaleString()} at once` : undefined}
        onChange={() => selection.toggle(id)} />
    </label>
  )
}

// The rows the grid holds now (an infinite grid's loaded blocks), for a
// selection that can't say "all matching" (its server takes ids only).
const loadedIds = (api, selection) => {
  const ids = []
  if (api && typeof api.forEachNode === 'function') api.forEachNode(node => { if (node && node.data) ids.push(selection.idOf(node.data)) })
  return ids
}

// The header's box: none, some (indeterminate, "mixed") or all matching. With
// nothing selected it selects all matching (or, ids only, the loaded rows);
// otherwise it deselects all. Its name says which. AG Grid moves the focus
// between header cells itself, so Space and Enter on the focused header cell
// work it too (suppressHeaderKeyboardEvent keeps AG Grid's own handling off).
const SelectHeader = props => {
  const { selection } = useColumnPropsOf(props.column && props.column.getColDef())
  const box = useRef(null)
  const state = selection ? selection.header : 'none'
  const act = useRef(() => {})
  act.current = () => {
    if (!selection) return
    if (state === 'none') selection.selectAll(loadedIds(props.api, selection))
    else selection.deselectAll()
  }
  useEffect(() => {
    if (box.current) box.current.indeterminate = state === 'some'
  }, [state])
  useEffect(() => {
    const cell = props.eGridHeader
    if (!cell || typeof cell.addEventListener !== 'function') return undefined
    const onKey = e => {
      if (e.target !== cell || (e.key !== ' ' && e.key !== 'Enter')) return
      e.preventDefault()
      act.current()
    }
    cell.addEventListener('keydown', onKey)
    return () => cell.removeEventListener('keydown', onKey)
  }, [props.eGridHeader])
  if (!selection) return null
  const name = state === 'none' ? (selection.canMatch ? 'Select all matching' : 'Select all') : 'Deselect all'
  return (
    <label className="tb-select" title={name}>
      <input ref={box} type="checkbox" className="form-check-input" aria-label={name} checked={state === 'all'}
        aria-checked={state === 'some' ? 'mixed' : state === 'all'} onChange={() => act.current()} />
    </label>
  )
}
const HEADER_KEYS = new Set([' ', 'Enter'])
const suppressSelectHeaderKeys = params => Boolean(params && params.event && HEADER_KEYS.has(params.event.key))

// What a column built from the page's functions needs: its key, and those
// functions for a grid without a ColumnPropsProvider.
const cellContext = (key, cell) => ({ context: { columnKey: key, cell } })

export const getColumns = children => {
  let items = Children.toArray(children)
  const keys = columnKeys(items)

  let columns = items.map((item, index) => {
    switch (item.type.name) {
      case ColumnIndex.name:
        return {
          field: 'Index',
          cellRenderer: renderIndex
        }

      case ColumnText.name:
        return { field: item.props.field, hide: item.props.hide, headerName: item.props.header, suppressHeaderMenuButton: true, sortable: item.props.sortable }

      case ColumnDate.name:
        return {
          field: item.props.field,
          headerName: item.props.header,
          hide: item.props.hide,
          suppressHeaderMenuButton: true,
          sortable: item.props.sortable,
          cellRenderer: renderDate
        }

      case ColumnDateTime.name:
        return {
          field: item.props.field,
          headerName: item.props.header,
          hide: item.props.hide,
          suppressHeaderMenuButton: true,
          sortable: item.props.sortable,
          cellRenderer: renderDateTime
        }

      case ColumnUnixDateTime.name:
        return {
          field: item.props.field,
          headerName: item.props.header,
          hide: item.props.hide,
          suppressHeaderMenuButton: true,
          sortable: item.props.sortable,
          cellRenderer: renderUnixDateTime
        }

      case ColumnSecond.name:
        return {
          field: item.props.field,
          headerName: item.props.header,
          hide: item.props.hide,
          suppressHeaderMenuButton: true,
          sortable: item.props.sortable,
          cellRenderer: renderSecond
        }

      case ColumnMilliSecond.name:
        return {
          field: item.props.field,
          headerName: item.props.header,
          hide: item.props.hide,
          suppressHeaderMenuButton: true,
          sortable: item.props.sortable,
          cellRenderer: renderMilliSecond
        }

      case ColumnDurationAuto.name:
        return {
          field: item.props.field,
          headerName: item.props.header,
          hide: item.props.hide,
          suppressHeaderMenuButton: true,
          sortable: item.props.sortable,
          // A cellRenderer (rather than a valueFormatter/valueGetter) leaves the
          // cell VALUE as the raw number, so sorting, filtering and CSV export
          // all still work off the underlying magnitude rather than off the
          // formatted string -- "900 ms" must not sort above "1.5 s".
          cellRenderer: renderDurationAuto
        }

      case ColumnDuration.name:
        return {
          field: item.props.field,
          headerName: item.props.header,
          hide: item.props.hide,
          suppressHeaderMenuButton: true,
          sortable: item.props.sortable,
          cellRenderer: durationRenderer(item.props.beganAt, item.props.endedAt)
        }

      case ColumnYesNo.name:
        return {
          field: item.props.field,
          headerName: item.props.header,
          hide: item.props.hide,
          suppressHeaderMenuButton: true,
          sortable: item.props.sortable,
          cellRenderer: renderYesNo
        }

      case ColumnSize.name:
        return {
          field: item.props.field,
          headerName: item.props.header,
          hide: item.props.hide,
          suppressHeaderMenuButton: true,
          cellRenderer: renderSize
        }

      case ColumnSelector.name:
        return {
          field: item.props.field,
          headerName: item.props.header,
          hide: item.props.hide,
          suppressHeaderMenuButton: true,
          sortable: item.props.sortable,
          cellRenderer: SelectorCell,
          comparator: item.props.comparator,
          ...cellContext(keys[index], { selector: item.props.selector })
        }

      case ColumnLink.name:
        return {
          field: item.props.field,
          headerName: item.props.header,
          hide: item.props.hide,
          suppressHeaderMenuButton: true,
          sortable: item.props.sortable,
          cellRenderer: LinkCell,
          ...cellContext(keys[index], { field: item.props.field, to: item.props.to })
        }

      case ColumnFormat.name:
        return {
          field: item.props.field,
          headerName: item.props.header,
          hide: item.props.hide,
          suppressHeaderMenuButton: true,
          sortable: item.props.sortable,
          // the page's own function, as it is: a module-level (or memoized)
          // render keeps its cells across renders, an inline one does not
          cellRenderer: item.props.render || renderFormatMissing,
          // comparator(a, b, nodeA, nodeB, isDescending): the column's own
          // sort order (app versions: "1.10.0" after "1.9.2").
          ...(item.props.comparator ? { comparator: item.props.comparator } : {}),
          // exportValue(rowData) -> text: what the CSV export writes for this
          // column (csvSafeCell applies valueFormatter). Without it the CSV
          // gets the raw field, which for an object reads [object Object].
          ...(item.props.exportValue ? { valueFormatter: params => (params.data ? item.props.exportValue(params.data) : '') } : {})
        }

      case ColumnSelect.name:
        return {
          colId: SELECT_COL_ID,
          headerName: '',
          headerComponent: SelectHeader,
          cellRenderer: SelectCell,
          ...cellContext(keys[index], { selection: item.props.selection }),
          sortable: false,
          pivot: false,
          enableRowGroup: false,
          resizable: false,
          suppressSizeToFit: true,
          suppressMovable: true,
          suppressHeaderMenuButton: true,
          suppressColumnsToolPanel: true,
          suppressHeaderKeyboardEvent: suppressSelectHeaderKeys,
          pinned: 'left',
          lockPinned: true,
          lockPosition: 'left',
          width: 52,
          minWidth: 52,
          maxWidth: 52
        }

      case ColumnMenu.name:
        return {
          colId: 'menu',
          field: 'menu',
          headerName: '',
          cellRenderer: MenuCell,
          ...cellContext(keys[index], { menu: item.props.menu }),
          sortable: false,
          pivot: false,
          enableRowGroup: false,
          suppressSizeToFit: true,
          resizable: false,
          suppressHeaderMenuButton: true,
          pinned: 'right',
          lockPinned: true,
          // Width is driven by table-ag.js (autoSizeColumns(['menu']), then
          // fitMenuColumn in table-sizing.js) so the action column holds each
          // table's inline icons; it never narrows within a mount.
          minWidth: 48
        }

      default:
        return {}
    }
  })

  return columns.map((column, index) => withSortKind(withLayout(column, items[index].props, items[index].type), items[index].type, items[index].props))
}

// What a column type holds, for the sort chip's words (sort-label.js), on the
// column definition's context (AG Grid's slot for application data).
// sortKind is certain from the type; sortKindFallback holds only when the
// grid's rows don't say otherwise (a ColumnText of ISO dates reads as dates).
const SORT_KIND = {
  [ColumnDate.name]: 'date',
  [ColumnDateTime.name]: 'date',
  [ColumnUnixDateTime.name]: 'date',
  [ColumnSecond.name]: 'number',
  [ColumnMilliSecond.name]: 'number',
  [ColumnDurationAuto.name]: 'number',
  [ColumnSize.name]: 'number'
}
const SORT_KIND_FALLBACK = { [ColumnText.name]: 'text', [ColumnLink.name]: 'text' }

const withSortKind = (column, type, props) => {
  const name = type && type.name
  // Any column may say what it holds (sortKind="date" | "number" | "text"),
  // e.g. a ColumnFormat over a date.
  if (props && props.sortKind) return { ...column, context: { ...column.context, sortKind: props.sortKind } }
  if (SORT_KIND[name]) return { ...column, context: { ...column.context, sortKind: SORT_KIND[name] } }
  if (SORT_KIND_FALLBACK[name]) return { ...column, context: { ...column.context, sortKindFallback: SORT_KIND_FALLBACK[name] } }
  return column
}

// The class a `stacked` column's cells carry (styles in table-ag.scss): the
// cell's content is laid out as a vertically centred block of normal line
// height, so a two-line cell (a name over a .sw-cell-sub subline, a logo tile
// beside them) fits a taller row (TableAG `rowHeight`) instead of every line
// taking the full row height and the second being clipped.
export const STACKED_CELL_CLASS = 'tb-cell-stack'

// What a column type holds, for its width (table-sizing.js): a short,
// structured value is always shown in full (FIT_CONTENT). Every other column
// -- text, links, a ColumnFormat's own rendering -- is free text (FIT_TEXT):
// shown in full up to TEXT_MAX_WIDTH, ellipsized beyond with the full value
// in a tooltip. A pill is never cut off either way.
const FIT_BY_TYPE = {
  [ColumnIndex.name]: FIT_CONTENT,
  [ColumnDate.name]: FIT_CONTENT,
  [ColumnDateTime.name]: FIT_CONTENT,
  [ColumnUnixDateTime.name]: FIT_CONTENT,
  [ColumnSecond.name]: FIT_CONTENT,
  [ColumnMilliSecond.name]: FIT_CONTENT,
  [ColumnDuration.name]: FIT_CONTENT,
  [ColumnDurationAuto.name]: FIT_CONTENT,
  [ColumnYesNo.name]: FIT_CONTENT,
  [ColumnSize.name]: FIT_CONTENT
}

// Per-column layout props any column may carry:
//   minWidth -- the narrowest the column may get (px). Every column is at
//               least as wide as its content and header anyway (table-sizing.js);
//               before anything is measured the grid's default is 100.
//   flex     -- its weight in sharing out the spare width when the columns
//               fit (default 1; the share is also in proportion to its width).
//   fit      -- "content" for a short, structured value that must never be cut
//               off (a ColumnFormat over a date, a status, a count); "text" for
//               free text. Defaults by column type (FIT_BY_TYPE).
//   textMax  -- a free-text column's own cap in place of TEXT_MAX_WIDTH (px),
//               for a list whose columns must fit a narrower window; past it
//               the text ellipsizes, and spare width still widens it.
//   stacked  -- the cell renders more than one line (see STACKED_CELL_CLASS).
const withLayout = (column, props, type) => {
  if (!props || column.colId === 'menu' || column.colId === SELECT_COL_ID) return column
  const layout = {}
  if (props.minWidth !== undefined) layout.minWidth = props.minWidth
  if (props.flex !== undefined) layout.flex = props.flex
  if (props.stacked) layout.cellClass = STACKED_CELL_CLASS
  const fit = props.fit || FIT_BY_TYPE[type && type.name] || FIT_TEXT
  const context = { ...column.context, fit }
  if (props.textMax !== undefined) context.textMax = props.textMax
  return { ...column, ...layout, context }
}

const Link = ({ text, to }) => {
  const history = useHistory()
  const handleClick = () => history.push(to)
  return (
    <span className="edxr-link-action" onClick={handleClick}>
      {text}
    </span>
  )
}

const SECOND_MS = 1000
const MINUTE_MS = 60 * SECOND_MS

// Below a second: whole milliseconds. Below a minute: seconds to one decimal.
// Above that: the same HH:mm:ss clock the other duration columns use.
const formatDurationAuto = value => {
  if (value === null || value === undefined || value === '') return ''

  const ms = Number(value)
  if (!isFinite(ms)) return ''

  const magnitude = Math.abs(ms)

  if (magnitude < SECOND_MS) return `${Math.round(ms)} ms`
  if (magnitude < MINUTE_MS) return `${(ms / SECOND_MS).toFixed(1)} s`

  return moment.utc(ms).format('HH:mm:ss')
}

var bytes = 8
var kilabyte = 1024
var megabyte = kilabyte * 1024
var gigabyte = megabyte * 1024

const formatSize = value => {
  const getSize = value => {
    if (value >= gigabyte) {
      return `${(value / gigabyte).toFixed(2)} GB`
    } else if (value >= megabyte) {
      return `${(value / megabyte).toFixed(2)} MB`
    } else if (value >= kilabyte) {
      return `${(value / kilabyte).toFixed(2)} KB`
    } else {
      return `${(value / bytes).toFixed(2)} KB`
    }
  }

  return (
    <span>
      {getSize(value)}
    </span>
  )
}
